import {Message} from 'element-ui';
import tConfig from './config'
import config from "./config";

class Socket {
    /**
     * @description: WebSocket 封装类
     * @param {String} url WebSocket 地址 (e.g. 'ws://localhost:8080')
     * @param {Function} messageCallback 消息接收回调 (data: Object|String|ArrayBuffer)
     * @param {Function} [errorCallback] 错误回调 (error: Event)
     * @param {Object} [options] 配置选项
     * @param {Number} [options.maxReconnectTimes=5] 最大重连次数
     * @param {Number} [options.reconnectInterval=3000] 重连间隔(ms)
     * @param {Number} [options.heartbeatInterval=30000] 心跳间隔(ms)
     * @param {String|Uint8Array} [options.heartbeatMsg='ping'] 心跳消息内容
     */
    constructor(
        url,
        messageCallback,
        errorCallback,
        {
            maxReconnectTimes = 5,
            reconnectInterval = 3000,
            heartbeatInterval = 30000,
            heartbeatMsg = 'ping',
        } = {}
    ) {
        this.url = `${config.wsUrl}?token=${window.localStorage.getItem('token')}`;
        this.messageCallback = messageCallback;
        this.errorCallback = errorCallback;
        this.onOpen = null; // 连接成功回调
        this.onClose = null; // 连接关闭回调

        // 配置参数
        this.maxReconnectTimes = maxReconnectTimes;
        this.reconnectInterval = reconnectInterval;
        this.heartbeatInterval = heartbeatInterval;
        this.heartbeatMsg = heartbeatMsg;

        // 内部状态
        this.socket = null;
        this.reconnectTimes = 0;
        this.isManualClose = false;
        this.heartbeatTimer = null;
        this.pendingMessages = []; // 连接未就绪时的消息队列
    }

    /**
     * @description: 建立 WebSocket 连接
     */
    connect() {
        if (this.socket) {
            this.close();
        }

        this.isManualClose = false;
        this.socket = new WebSocket(this.url);

        this.socket.binaryType = 'arraybuffer'; // 支持二进制数据

        this.socket.onopen = () => {
            console.log(`[WebSocket] 连接成功 (${this.url})`);
            this.reconnectTimes = 0;
            this.startHeartbeat();

            // 处理等待中的消息
            this.flushPendingMessages();

            if (this.onOpen) this.onOpen();
        };

        this.socket.onmessage = (event) => {
            this.handleMessage(event);
        };

        this.socket.onclose = () => {
            console.log('[WebSocket] 连接关闭');
            this.stopHeartbeat();
            if (!this.isManualClose && this.reconnectTimes < this.maxReconnectTimes) {
                this.reconnect();
            }
            if (this.onClose) this.onClose();
        };

        this.socket.onerror = (error) => {
            console.error('[WebSocket] 连接错误:', error);
            this.stopHeartbeat();
            if (this.errorCallback) this.errorCallback(error);
        };
    }

    /**
     * @description: 处理接收到的消息
     * @param {MessageEvent} event
     */
    handleMessage(event) {
        if (!this.messageCallback) return;

        try {
            let data = event.data;

            // 二进制数据处理 (ArrayBuffer)
            if (data instanceof ArrayBuffer) {
                data = new Uint8Array(data);
            }
            // 文本数据 (JSON 或纯文本)
            else if (typeof data === 'string') {
                try {
                    data = JSON.parse(data);
                } catch (e) {
                    // 保持为纯文本
                }
            }

            this.messageCallback(data);
        } catch (e) {
            console.error('[WebSocket] 消息解析错误:', e);
        }
    }

    /**
     * @description: 发送消息
     * @param {Object|String|ArrayBuffer|Uint8Array} data
     */
    send(data) {
        if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
            console.warn('[WebSocket] 连接未就绪，消息进入队列');
            this.pendingMessages.push(data);
            return;
        }

        try {
            // 处理二进制数据
            if (data instanceof Uint8Array) {
                this.socket.send(data.buffer);
            }
            // 处理对象 (转为JSON)
            else if (typeof data === 'object') {
                this.socket.send(JSON.stringify(data));
            }
            // 文本或二进制数据
            else {
                this.socket.send(data);
            }
        } catch (e) {
            console.error('[WebSocket] 消息发送失败:', e);
        }
    }

    /**
     * @description: 发送队列中的消息
     */
    flushPendingMessages() {
        while (this.pendingMessages.length > 0) {
            const msg = this.pendingMessages.shift();
            this.send(msg);
        }
    }

    /**
     * @description: 开始心跳检测
     */
    startHeartbeat() {
        this.stopHeartbeat();
        this.heartbeatTimer = setInterval(() => {
            if (this.socket?.readyState === WebSocket.OPEN) {
                try {
                    // 支持二进制心跳 (e.g. 0x9, 0x00)
                    if (this.heartbeatMsg instanceof Uint8Array) {
                        this.socket.send(this.heartbeatMsg.buffer);
                    } else {
                        this.socket.send(this.heartbeatMsg);
                    }
                    console.debug('[WebSocket] 心跳发送');
                } catch (e) {
                    console.error('[WebSocket] 心跳发送失败:', e);
                }
            }
        }, this.heartbeatInterval);
    }

    /**
     * @description: 停止心跳检测
     */
    stopHeartbeat() {
        if (this.heartbeatTimer) {
            clearInterval(this.heartbeatTimer);
            this.heartbeatTimer = null;
        }
    }

    /**
     * @description: 重新连接
     */
    reconnect() {
        this.reconnectTimes++;
        Message.warning(`[WebSocket] 尝试第 ${this.reconnectTimes} 次重连...`)
        console.log(`[WebSocket] 尝试第 ${this.reconnectTimes} 次重连...`);
        setTimeout(() => this.connect(), this.reconnectInterval);
    }

    /**
     * @description: 主动关闭连接
     */
    close() {
        this.isManualClose = true;
        this.stopHeartbeat();
        this.pendingMessages = [];
        if (this.socket) {
            this.socket.close();
        }
    }

    /**
     * @description: 获取当前状态
     * @return {Number} WebSocket 状态 (0: CONNECTING, 1: OPEN, 2: CLOSING, 3: CLOSED)
     */
    getState() {
        return this.socket?.readyState ?? WebSocket.CLOSED;
    }
}

export default Socket;